import {useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {Row, Col} from 'react-bootstrap';
import {PageTitle} from '../../../_metronic/layout/core';
import CardComponent from '../../common/components/CardComponent';
import EditSubscriptionModal, {EditSubscriptionData} from './modals/EditSubscriptionModal';
import styles from './SubscriptionDetail.module.scss';

interface SubscriptionDetailData {
  id: number;
  reference: string;
  name: string;
  status: string;
  productName: string;
  productDescription: string;
  siteName: string;
  siteAddress: string;
  startDate: string;
  endDate: string;
  monthlyPrice: string;
  contractDuration: string;
  autoRenewal: boolean;
  billingPeriod: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
}

const SubscriptionDetail = () => {
  const {id} = useParams<{id: string}>();
  const intl = useIntl();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);

  const subscription: SubscriptionDetailData = {
    id: Number(id),
    reference: 'SUB-2024-001',
    name: 'Abonnement Fibre Pro',
    status: 'Actif',
    productName: 'Fibre 1Gb/s',
    productDescription: 'Connexion fibre optique professionnelle avec garantie de temps de rétablissement',
    siteName: 'Site Paris',
    siteAddress: '123 Avenue des Champs-Élysées, 75008 Paris',
    startDate: '01/01/2024',
    endDate: '31/12/2024',
    monthlyPrice: '299,00 €',
    contractDuration: '12 mois',
    autoRenewal: true,
    billingPeriod: 'Mensuel',
    contactName: 'Jean Dupont',
    contactEmail: 'jean.dupont@example.com',
    contactPhone: '+33 1 23 45 67 89',
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Actif':
        return '#28a745';
      case 'Inactif':
        return '#dc3545';
      case 'En attente':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  const handleSaveSubscription = (data: EditSubscriptionData) => {
    console.log('Données mises à jour:', data);
    
    // await updateSubscription(id, data);

    alert('Abonnement mis à jour avec succès !');
  };

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({id: 'MENU.SUBSCRIPTION_DETAIL'})}
      </PageTitle>

      <div className="d-flex flex-column gap-5">
        {/* Bouton retour */}
        <div>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/my-account/subscriptions')}
          >
            <i className="fa fa-arrow-left me-2"></i>
            Retour à la liste
          </button>
        </div>

        {/* Informations générales */}
        <Row xs={1} md={2}>
          <Col>
            <CardComponent title="Informations générales">
              <div className={styles.cardContainer}>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Référence</label>
                  <span className={styles.value}>{subscription.reference}</span>
                </div>
                <div className={styles.separator}></div>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Nom de l'abonnement</label>
                  <span className={styles.value}>{subscription.name}</span>
                </div>
                <div className={styles.separator}></div>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Statut</label>
                  <button
                    className={styles.statusBadge}
                    style={{backgroundColor: getStatusColor(subscription.status)}}
                    disabled
                  >
                    {subscription.status}
                  </button>
                </div>
              </div>
            </CardComponent>
          </Col>

          <Col>
            <CardComponent title="Dates et durée">
              <div className={styles.cardContainer}>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Date de début</label>
                  <span className={styles.value}>{subscription.startDate}</span>
                </div>
                <div className={styles.separator}></div>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Date de fin</label>
                  <span className={styles.value}>{subscription.endDate}</span>
                </div>
                <div className={styles.separator}></div>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Durée du contrat</label>
                  <span className={styles.value}>{subscription.contractDuration}</span>
                </div>
              </div>
            </CardComponent>
          </Col>
        </Row>

        <Row xs={1} md={2}>
          <Col>
            <CardComponent title="Détails de l'offre">
              <div className={styles.cardContainer}>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Produit</label>
                  <span className={styles.value}>{subscription.productName}</span>
                </div>
                <div className={styles.separator}></div>
                <div className={styles.detailRowStart}>
                  <label className={styles.label}>Description</label>
                  <span className={styles.valueDescription}>
                    {subscription.productDescription}
                  </span>
                </div>
              </div>
            </CardComponent>
          </Col>

          <Col>
            <CardComponent title="Informations de facturation">
              <div className={styles.cardContainer}>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Prix mensuel</label>
                  <span className={styles.priceValue}>{subscription.monthlyPrice}</span>
                </div>
                <div className={styles.separator}></div>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Période de facturation</label>
                  <span className={styles.value}>{subscription.billingPeriod}</span>
                </div>
                <div className={styles.separator}></div>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Renouvellement automatique</label>
                  <span className={styles.value}>
                    {subscription.autoRenewal ? (
                      <span className="badge badge-light-success">Activé</span>
                    ) : (
                      <span className="badge badge-light-danger">Désactivé</span>
                    )}
                  </span>
                </div>
              </div>
            </CardComponent>
          </Col>
        </Row>

        <Row xs={1} md={2}>
          <Col>
            <CardComponent title="Informations du site">
              <div className={styles.cardContainer}>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Nom du site</label>
                  <span className={styles.value}>{subscription.siteName}</span>
                </div>
                <div className={styles.separator}></div>
                <div className={styles.detailRowStart}>
                  <label className={styles.label}>Adresse</label>
                  <span className={styles.valueDescription}>
                    {subscription.siteAddress}
                  </span>
                </div>
              </div>
            </CardComponent>
          </Col>

          <Col>
            <CardComponent title="Contact">
              <div className={styles.cardContainer}>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Nom</label>
                  <span className={styles.value}>{subscription.contactName}</span>
                </div>
                <div className={styles.separator}></div>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Email</label>
                  <a href={`mailto:${subscription.contactEmail}`} className={`${styles.value} text-primary`}>
                    {subscription.contactEmail}
                  </a>
                </div>
                <div className={styles.separator}></div>
                <div className={styles.detailRow}>
                  <label className={styles.label}>Téléphone</label>
                  <a href={`tel:${subscription.contactPhone}`} className={`${styles.value} text-primary`}>
                    {subscription.contactPhone}
                  </a>
                </div>
              </div>
            </CardComponent>
          </Col>
        </Row>

        <div className={styles.actionsContainer}>
          <button 
            className="btn btn-primary"
            onClick={() => setShowEditModal(true)}
          >
            <i className="fa fa-edit me-2"></i>
            Modifier l'abonnement
          </button>
          <button className="btn btn-light-danger">
            <i className="fa fa-times me-2"></i>
            Résilier l'abonnement
          </button>
        </div>
      </div>

      <EditSubscriptionModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        onSave={handleSaveSubscription}
        subscription={{
          id: subscription.id,
          name: subscription.name,
          reference: subscription.reference,
          startDate: subscription.startDate,
          endDate: subscription.endDate,
          autoRenewal: subscription.autoRenewal,
          status: subscription.status,
          siteName: subscription.siteName,
          siteAddress: subscription.siteAddress,
          contactName: subscription.contactName,
          contactEmail: subscription.contactEmail,
          contactPhone: subscription.contactPhone,
          billingPeriod: subscription.billingPeriod,
          productName: subscription.productName,
          productDescription: subscription.productDescription,
        }}
      />


    </>
  );
};

export default SubscriptionDetail;
